import { useState, useEffect } from "react";
import { Layout, Menu, Icon, Avatar, Dropdown, notification } from "antd";
import { Route, Link, useLocation, Redirect } from "react-router-dom";
import "antd/dist/antd.less";
import "./index.css";
import "./index.scss";

import api from "../../services/api";
import firebase from "../../services/firebaseConfig";
import { appEnv } from "../../appEnv";
import { ThemeProvider, useTheme } from "../../components/ThemeProvider";
import { getUserPermissions } from "../../services/auth";
import { Show, useAccess } from "../../contexts/PermissionContext";
import ModalMakeAdmin from "./ModalMakeAdmin";
import MenuConfig from "./MenuConfig";
import RoutesDashboardConfig from "./RoutesDashboardConfig";
import AppModals from "../../components/AppModals";
import { useFreire } from "../../utils/freireContext";
import * as texts from "./locales";
import { LANGUAGES } from "../../config/languages";
import storedPreferencesHandler from "../../utils/stored-preferences-handler";

const { Header, Sider, Content } = Layout;
const { SubMenu } = Menu;

const DEFAULT_INITIAL_ROUTE_PATH = "/dashboard/connected_stations";

const menuList = {
  list_stations: "stations",
  connected_stations: "stations",
  audit_stations: "stations",
  batch_operation: "stations",
  visibility_group: "visibility",
  new_visibility_group: "visibility",
  admin_users: "admin",
  companys: "admin",
  recipients: "admin",
  plugs: "admin",
  powerbi: "admin",
  "pix-health-check": "admin",
  permission_groups: "admin",
  report_groups: "report",
  new_report_group: "report",
  charges_history: "history",
  payment_history: "history",
  availability_stations: "availability",
  availability_system: "availability",
  stations: "ocpp",
  transactions: "ocpp",
  tags: "ocpp",
  actions: "ocpp",
  alert_groups: "ocpp",
  logs: "ocpp",
  monitoring: "ocpp",
};

const DashboardContent = ({ match, history }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [clickedRedirectUrl, setClickedRedirectUrl] = useState(null);
  const [username, setUsername] = useState("");
  const [foto, setFoto] = useState("");
  const [selectedKey, setSelectedKey] = useState([]);
  const [openKeys, setOpenKeys] = useState([]);
  const { theme: themeConfig, setThemeToDefault } = useTheme();
  const { define, isLoaded, hasPermission, initialRoutePath } = useAccess();
  const [visibleModalAdmin, setVisibleModalAdmin] = useState(false);
  const [loadingSaveAdmin, setLoadingSaveAdmin] = useState(false);
  const { freire } = useFreire();

  const toggle = () => {
    setCollapsed(!collapsed);
  };

  const logout = async () => {
    try {
      setThemeToDefault();
      await firebase.auth().signOut();

      storedPreferencesHandler.clearAll();

      define({
        isLoaded: false,
        permissions: {},
        initialRoutePath: null,
        resourcePolicies: [],
        featureFlags: {},
      });
    } catch (error) {
      console.log("Error on logout:", error);
    } finally {
      history.push("/");
    }
  };

  const saveAdmin = async (data, callbackSuccess) => {
    setLoadingSaveAdmin(true);

    try {
      await api.post(`/user/company`, data);

      notification.success({
        message: freire(texts.ALL_RIGHT),
        description: freire(texts.OPERATION_SUCCESSFULLY),
      });

      callbackSuccess();
      setVisibleModalAdmin(false);
    } catch (error) {
      notification.error({
        message: "Ops...",
        description: error.response.error || freire(texts.SOMETHING_WRONG),
      });
    } finally {
      setLoadingSaveAdmin(false);
    }
  };

  useEffect(() => {
    const currentUser = firebase.auth().currentUser.toJSON();

    async function fetchData() {
      const {
        permissions,
        initialRoutePath,
        permissionGuideUrl,
        resourcePolicies,
        featureFlags,
      } = await getUserPermissions();

      define({
        isLoaded: true,
        permissions,
        initialRoutePath,
        permissionGuideUrl,
        resourcePolicies,
        featureFlags,
      });
    }

    if (currentUser) {
      if (currentUser.displayName) {
        setUsername(currentUser.displayName);
      } else {
        setUsername(currentUser.email);
      }

      if (currentUser.photoURL) {
        setFoto(currentUser.photoURL);
      }
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { state } = useLocation();

  useEffect(() => {
    const split = (initialRoutePath ?? DEFAULT_INITIAL_ROUTE_PATH).split("/");
    const key = split[split.length - 1];

    setSelectedKey([key]);
    setOpenKeys([menuList[key]]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRoutePath]);

  const menu = (setLanguage) => (
    <Menu style={{ marginTop: 10 }}>
      {hasPermission("make-user-admin-dropdown") && (
        <Menu.Item onClick={() => setVisibleModalAdmin(true)}>
          {freire(texts.MAKE_USER_ADMIN)}
        </Menu.Item>
      )}
      <SubMenu title={freire(texts.LANGUAGE)}>
        {Object.entries(LANGUAGES).map(([languageCode, language]) => (
          <Menu.Item key={languageCode} onClick={setLanguage(languageCode)}>
            {freire(language.text)}
          </Menu.Item>
        ))}
      </SubMenu>
      <Menu.Item onClick={logout}>{freire(texts.EXIT)}</Menu.Item>
    </Menu>
  );

  const getLogo = () => {
    return collapsed
      ? themeConfig.logos.collapsed
      : themeConfig.logos.notCollapsed;
  };

  const setLanguage = (languageCode) => () => {
    freire.setUserLanguage(languageCode);
  };

  const handleRedirect = async (redirectUrl) => {
    if (clickedRedirectUrl === redirectUrl) return;

    try {
      setClickedRedirectUrl(redirectUrl);

      const { data } = await api.post("/auth/custom-token");

      if (!data?.customToken) {
        throw new Error("Custom token not found");
      }

      const urlWithToken = `${redirectUrl}?customToken=${encodeURIComponent(
        data.customToken
      )}`;

      window.open(urlWithToken, "_blank");
    } catch (error) {
      console.error("Error on handleRedirect", error);

      // abre o link direto sem o customToken
      window.open(redirectUrl, "_blank");
    } finally {
      setClickedRedirectUrl(null);
    }
  };

  if (!isLoaded) {
    return (
      <div
        style={{
          display: "flex",
          width: "100vw",
          height: "100vh",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Icon type="loading" style={{ fontSize: "48px" }} />
      </div>
    );
  }

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider theme="dark" trigger={null} collapsible collapsed={collapsed}>
        <div className="logo">
          {hasPermission("dashboard-powerbi") ? (
            <Link to={"/dashboard"}>
              <img className="img-logo" alt="Logo" src={getLogo()} />
            </Link>
          ) : (
            <img className="img-logo" alt="Logo" src={getLogo()} />
          )}
        </div>
        <Menu
          openKeys={openKeys}
          selectedKeys={selectedKey}
          onOpenChange={(keys) => setOpenKeys(keys)}
          onSelect={(params) => setSelectedKey([params.key])}
          mode="inline"
          theme="dark"
          style={{
            background: themeConfig.menuBackgroundColor,
            color: themeConfig.menuTextColor,
          }}
        >
          {MenuConfig(match.url).map((item) => {
            if (hasPermission(item.permission)) {
              if (item.type === "submenu") {
                return (
                  <SubMenu
                    key={item.key}
                    title={
                      <span>
                        <Icon type={item.icon} />
                        <span>{freire(texts[item.title])}</span>
                      </span>
                    }
                  >
                    {item.submenus.map((submenu) => {
                      if (hasPermission(submenu.permission)) {
                        if (submenu.redirect) {
                          const isRedirecting =
                            clickedRedirectUrl === submenu.redirect;
                          return (
                            <span
                              className="redirect-link"
                              style={{
                                cursor: isRedirecting
                                  ? "not-allowed"
                                  : "pointer",
                              }}
                              onClick={() => handleRedirect(submenu.redirect)}
                            >
                              {isRedirecting && (
                                <Icon
                                  type="loading"
                                  style={{ fontSize: "10px", marginRight: 5 }}
                                />
                              )}
                              <span>{freire(texts[submenu.title])}</span>
                              {submenu.tag && (
                                <span className="tag">{submenu.tag}</span>
                              )}
                            </span>
                          );
                        }
                        return (
                          <Menu.Item key={submenu.key}>
                            <Link to={submenu.linkTo}>
                              <Icon type={submenu.icon} />
                              <span>{freire(texts[submenu.title])}</span>
                            </Link>
                          </Menu.Item>
                        );
                      }

                      return null;
                    })}
                  </SubMenu>
                );
              }

              if (item.type === "menu") {
                return (
                  <Menu.Item key={item.key}>
                    <Link to={item.linkTo}>
                      <Icon type={item.icon} />
                      <span>{freire(texts[item.title])}</span>
                    </Link>
                  </Menu.Item>
                );
              }
            }

            return null;
          })}
        </Menu>
      </Sider>
      <Layout>
        <Header
          style={{
            background: themeConfig.menuBackgroundColor,
            padding: 0,
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <Icon
            className="trigger"
            type={collapsed ? "menu-unfold" : "menu-fold"}
            onClick={toggle}
            style={{ color: themeConfig.menuTextColor }}
          />

          {appEnv.REACT_APP_SHOW_ENV_NAME === "true" && (
            <span
              style={{
                position: "absolute",
                top: -2,
                marginLeft: 10,
                color: "red",
                fontWeight: "bold",
                fontSize: 30,
              }}
            >
              {appEnv.REACT_APP_ENV_NAME.toUpperCase()}
            </span>
          )}
          <div className="perfil">
            <Dropdown overlay={menu(setLanguage)}>
              <span style={{ color: themeConfig.menuSelectedTextColor }}>
                {foto ? <Avatar src={foto} /> : <Avatar icon="user" />}
                <span
                  style={{ marginLeft: 8, color: themeConfig.menuTextColor }}
                >
                  {username || freire(texts.USER)}
                </span>
              </span>
            </Dropdown>
          </div>
        </Header>
        <Content style={{ color: themeConfig.menuTextColor }}>
          {RoutesDashboardConfig(match.url).map((route) => (
            <Show key={route.path + route.permission} when={route.permission}>
              <Route
                path={route.path}
                exact={route.exact}
                component={route.component}
              />
            </Show>
          ))}

          {state?.from === "login" && (
            <Redirect
              to={{ pathname: initialRoutePath ?? DEFAULT_INITIAL_ROUTE_PATH }}
            />
          )}
        </Content>
      </Layout>
      <ModalMakeAdmin
        visible={visibleModalAdmin}
        loading={loadingSaveAdmin}
        onClose={() => setVisibleModalAdmin(false)}
        onSave={saveAdmin}
      />

      <AppModals />
    </Layout>
  );
};

export default function Dashboard({ match, history }) {
  return (
    <ThemeProvider>
      <DashboardContent match={match} history={history} />
    </ThemeProvider>
  );
}
